/**
 * Typed save data for Supermarket Kart Racing.
 *
 * This document is the contract a future remote repository can sync.
 * Gameplay code reads and writes a `PlayerProfile` only — it does not
 * know whether the bytes live in localStorage or in MemoCart's API.
 */

export const PROFILE_VERSION = 1;

export const PHASE_IDS = [
  'cidade',
  'deserto',
  'neve',
  'esgoto',
  'terra',
  'base',
  'espaco',
] as const;

export type PhaseId = (typeof PHASE_IDS)[number];

export type Medal = 'none' | 'bronze' | 'silver' | 'gold';

export interface GameSettings {
  graphics: 'low' | 'high';
  /** 0..1 */
  volume: number;
  camera: 'chase' | 'high';
  cameraShake: boolean;
  /** Nose dip on brake, accel, landing and rough patches. Off by default. */
  frontBounce: boolean;
}

export interface MedalThresholds {
  /** Race time ceilings in milliseconds, inclusive. */
  gold: number;
  silver: number;
  bronze: number;
}

export interface PhaseResult {
  phaseId: PhaseId;
  completed: boolean;
  bestTimeMs: number | null;
  bestLapMs: number | null;
  medal: Medal;
  attempts: number;
  finishes: number;
  lastPlayedAt: string | null;
}

export interface PlayerProfile {
  version: number;
  displayName: string;
  settings: GameSettings;
  phases: Record<PhaseId, PhaseResult>;
  updatedAt: string;
}

export const STORAGE_KEY = 'memocart.skr.profile.v1';

const MEDAL_RANK: Record<Medal, number> = {
  none: 0,
  bronze: 1,
  silver: 2,
  gold: 3,
};

export function defaultSettings(): GameSettings {
  return {
    graphics: 'high',
    volume: 0.7,
    camera: 'chase',
    cameraShake: true,
    frontBounce: false,
  };
}

export function emptyPhaseResult(phaseId: PhaseId): PhaseResult {
  return {
    phaseId,
    completed: false,
    bestTimeMs: null,
    bestLapMs: null,
    medal: 'none',
    attempts: 0,
    finishes: 0,
    lastPlayedAt: null,
  };
}

export function createDefaultProfile(): PlayerProfile {
  const phases = {} as Record<PhaseId, PhaseResult>;
  for (const id of PHASE_IDS) phases[id] = emptyPhaseResult(id);
  return {
    version: PROFILE_VERSION,
    displayName: 'Jogador',
    settings: defaultSettings(),
    phases,
    updatedAt: new Date().toISOString(),
  };
}

export function medalForTime(timeMs: number, thresholds: MedalThresholds): Medal {
  if (timeMs <= thresholds.gold) return 'gold';
  if (timeMs <= thresholds.silver) return 'silver';
  if (timeMs <= thresholds.bronze) return 'bronze';
  return 'none';
}

export function bestMedal(a: Medal, b: Medal): Medal {
  return MEDAL_RANK[a] >= MEDAL_RANK[b] ? a : b;
}

/**
 * Medal times from race distance. Averages are arcade targets:
 * gold ~13 m/s, silver ~10.2 m/s, bronze ~8 m/s including corners.
 */
export function medalsForDistance(lapLength: number, laps: number, pace?: MedalThresholds): MedalThresholds {
  if (pace) return pace;
  const meters = Math.max(1, lapLength * laps);
  return {
    gold: Math.round((meters / 13) * 1000),
    silver: Math.round((meters / 10.2) * 1000),
    bronze: Math.round((meters / 8) * 1000),
  };
}

function isMedal(value: unknown): value is Medal {
  return value === 'none' || value === 'bronze' || value === 'silver' || value === 'gold';
}

export function sanitizeProfile(raw: unknown): PlayerProfile {
  const profile = createDefaultProfile();
  if (!raw || typeof raw !== 'object') return profile;
  const data = raw as Partial<PlayerProfile>;
  if (data.version !== PROFILE_VERSION) return profile;

  if (typeof data.displayName === 'string' && data.displayName.trim()) {
    profile.displayName = data.displayName.trim().slice(0, 24);
  }
  if (typeof data.updatedAt === 'string') profile.updatedAt = data.updatedAt;

  const settings = data.settings;
  if (settings && typeof settings === 'object') {
    profile.settings.graphics = settings.graphics === 'low' ? 'low' : 'high';
    const volume = Number(settings.volume);
    profile.settings.volume = Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : 0.7;
    profile.settings.camera = settings.camera === 'high' ? 'high' : 'chase';
    profile.settings.cameraShake = settings.cameraShake !== false;
    profile.settings.frontBounce = settings.frontBounce === true;
  }

  if (data.phases && typeof data.phases === 'object') {
    for (const id of PHASE_IDS) {
      const entry = data.phases[id];
      if (!entry || typeof entry !== 'object') continue;
      const bestTimeMs = typeof entry.bestTimeMs === 'number' && entry.bestTimeMs >= 0 ? entry.bestTimeMs : null;
      const bestLapMs = typeof entry.bestLapMs === 'number' && entry.bestLapMs >= 0 ? entry.bestLapMs : null;
      profile.phases[id] = {
        phaseId: id,
        completed: entry.completed === true,
        bestTimeMs,
        bestLapMs,
        medal: isMedal(entry.medal) ? entry.medal : 'none',
        attempts: Number.isFinite(entry.attempts) ? Math.max(0, Math.floor(entry.attempts)) : 0,
        finishes: Number.isFinite(entry.finishes) ? Math.max(0, Math.floor(entry.finishes)) : 0,
        lastPlayedAt: typeof entry.lastPlayedAt === 'string' ? entry.lastPlayedAt : null,
      };
    }
  }

  return profile;
}
